import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { axios } from "~/core/axios";
import { REACT_QUERY_KEY } from "~/core/constants/common";
import { identifyUserForAnalytics } from "~/core/posthog/browser-client";
import type { BackendApiResponse } from "~/core/types/backend-api-response.type";

import { GetUserAndOrgDataResponse } from "~/lib/user-session/types";

/**
 * Hook to fetch user session data from the /user/me API endpoint
 * This provides more detailed user information than the JWT token
 */
export default function useUserSession() {
  const {
    data: userSession,
    isLoading: loadingUserSession,
    isError: errorLoadingUserSession,
    error: errorUserSession,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEY.USER_SESSION],
    queryFn: async () => {
      try {
        const { data } =
          await axios.get<BackendApiResponse<GetUserAndOrgDataResponse>>(
            "/auth/me",
          );
        return data.data;
      } catch (error) {
        throw new Error("Failed to fetch user session data");
      }
    },
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!userSession?.user?.email) return;

    identifyUserForAnalytics({
      email: userSession?.user?.email,
      name: userSession?.user?.display_name ?? "",
      user_id: userSession?.user?.user_id ?? "",
      workspace_id: userSession?.org_id ?? "",
      domain: userSession?.organizations?.[0]?.domain ?? "",
    });
  }, [userSession]);

  return {
    userSession,
    loadingUserSession,
    errorLoadingUserSession,
    errorUserSession,
    refetch,
  };
}

export const useInvalidateUserSessionQuery = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEY.USER_SESSION],
    });
  };
};
